import app from '../../authentication/firebaseStart';
import { ROLES } from '../../authentication/rbac';

const firestore = app.firestore();

// Helper function to execute queries and get total count
export const getTotalCount = async (context, { searchText, searchField, orderDirection }) => {
  try {
    if (searchText && searchField) {
      // For search queries, get the total count of matching documents
      const totalQuery = createQuery({ 
        context, 
        searchText, 
        searchField,
        orderBy: searchField,
        orderDirection 
      });
      const countSnapshot = await totalQuery.get();
      return countSnapshot.size;
    } else {
      // For non-search queries, use the total kiosk count
      return await getKioskCount(
        context.currentUser.claims.companyID,
        context.currentUser.claims
      );
    }
  } catch (error) {
    console.error('Error getting total count:', error);
    throw error;
  }
};

// Helper functions for query execution
const executeQuery = async (query) => {
  const snapshot = await query.get();
  return {
    docs: snapshot.docs,
    kiosks: snapshot.docs.map(doc => ({
      __name__: doc.id,
      ...doc.data()
    })),
    cursors: {
      first: snapshot.docs[0] || null,
      last: snapshot.docs[snapshot.docs.length - 1] || null
    }
  };
};

// Apply pagination to query
const applyPagination = (query, { startAfterDoc, endBeforeDoc, startAtDoc, limit }) => {
  let paginatedQuery = query;

  if (startAtDoc) {
    paginatedQuery = paginatedQuery.startAt(startAtDoc);
  } else if (startAfterDoc) {
    paginatedQuery = paginatedQuery.startAfter(startAfterDoc);
  } else if (endBeforeDoc) {
    paginatedQuery = paginatedQuery.endBefore(endBeforeDoc);
  }

  if (limit) {
    paginatedQuery = endBeforeDoc ? 
      paginatedQuery.limitToLast(limit) : 
      paginatedQuery.limit(limit);
  }

  return paginatedQuery;
};

// Create query with search and filters
const createQuery = ({ context, searchText, searchField, orderBy, orderDirection }) => {
  const { companyID, isManager, siteName } = context.currentUser.claims;
  let query = firestore
    .collection('tenants')
    .doc(companyID)
    .collection('Kiosk');

  // Apply site filter for site admin/manager
  if (isManager) {
    query = query.where('siteName', '==', siteName);
  }

  if (searchText && searchField) {
    const searchTerm = searchText.trim();
    const endTerm = searchTerm + '\uf8ff';
    
    query = query
      .where(searchField, '>=', searchTerm)
      .where(searchField, '<=', endTerm)
      .orderBy(searchField, orderDirection);
  } else {
    query = query.orderBy(orderBy, orderDirection);
  }

  return query;
};

// Main function to fetch kiosks with pagination
export const fetchKiosks = async ({
  limit,
  startAfterDoc = null,
  endBeforeDoc = null,
  startAtDoc = null,
  orderBy = 'kioskName',
  orderDirection = 'asc',
  searchText = null,
  searchField = null,
  context,
  totalCount = null
}) => {
  console.log(`Fetching kiosks with params:`, {
    limit,
    orderBy,
    orderDirection,
    searchText,
    searchField,
    totalCount,
    hasStartAfter: !!startAfterDoc,
    hasEndBefore: !!endBeforeDoc,
    hasStartAt: !!startAtDoc
  });

  try {
    const query = createQuery({ 
      context, 
      searchText, 
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection 
    });

    const paginatedQuery = applyPagination(query, {
      startAfterDoc,
      endBeforeDoc,
      startAtDoc,
      limit
    });

    const { kiosks, cursors } = await executeQuery(paginatedQuery);
    
    const count = totalCount ?? await getTotalCount(context, { searchText, searchField, orderDirection });
    console.log(kiosks, count)
    return {
      kiosks,
      totalCount: count,
      cursors
    };
  } catch (error) {
    console.error('Error fetching kiosks:', error);
    throw error;
  }
};

// Fetch last page of kiosks
export const fetchLastPage = async ({
  context,
  limit,
  searchText = null,
  searchField = null,
  orderBy = 'kioskName',
  orderDirection = 'asc',
  totalCount = null
}) => {
  try {
    const count = totalCount ?? await getTotalCount(context, { searchText, searchField, orderDirection });

    const totalPages = Math.floor(count / limit);
    const remainingRecords = count - (totalPages * limit);
    const lastPageLimit = remainingRecords > 0 ? remainingRecords : limit;

    const query = createQuery({
      context,
      searchText,
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection
    });

    const snapshot = await query.limitToLast(lastPageLimit).get();

    return {
      kiosks: snapshot.docs.map(doc => ({
        __name__: doc.id,
        ...doc.data()
      })),
      cursors: {
        first: snapshot.docs[0] || null,
        last: snapshot.docs[snapshot.docs.length - 1] || null
      },
      totalCount: count
    };
  } catch (error) {
    console.error('Error fetching last page:', error);
    throw error;
  }
};

// Get total kiosk count
export const getKioskCount = async (companyID, claims) => {
  try {

    const { isManager, siteName } = claims

    let query = firestore
      .collection('tenants')
      .doc(companyID)
      .collection('Kiosk')

    if (isManager) {
      query = query.where('siteName', '==', siteName);
    }

    let kiosks = await query.get()
    kiosks = kiosks.docs.map((doc) => doc.data())

    return kiosks.length || 0;

  } catch (error) {
    console.error('Error fetching kiosk count:', error);
    throw error;
  }
};

export default {
  fetchKiosks,
  fetchLastPage,
  getKioskCount,
  getTotalCount
};